import React, { useMemo } from "react";

export interface PickerOption {
  value: string;
  label: string;
}

export interface PickerGroup {
  heading?: string;
  options: PickerOption[];
}

interface Props {
  groups: PickerGroup[];
  rowIcon: React.ReactNode;
  value: string;
  query?: string;
  onSelect: (option: PickerOption) => void;
}

export default function PickerList({ groups, rowIcon, value, query = "", onSelect }: Props) {
  const selectedLabel = useMemo(() => {
    if (!value) return "";
    const match = groups.flatMap((group) => group.options).find((option) => option.value === value);
    return match?.label ?? value;
  }, [groups, value]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle || needle === selectedLabel.trim().toLowerCase()) return groups;

    return groups
      .map((group) => ({
        ...group,
        options: group.options.filter((option) => option.label.toLowerCase().includes(needle)),
      }))
      .filter((group) => group.options.length > 0);
  }, [groups, query, selectedLabel]);

  if (filtered.length === 0) return <div className="v2-picker__empty">No matches found.</div>;

  return (
    <>
      {filtered.map((group, index) => (
        <div className="v2-picker__group" key={group.heading || index}>
          {group.heading && <div className="v2-picker__heading">{group.heading}</div>}
          {group.options.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`v2-picker__row ${option.value === value ? "is-active" : ""}`}
              onClick={() => onSelect(option)}
            >
              <span className="v2-picker__row-icon">{rowIcon}</span>
              {option.label}
            </button>
          ))}
        </div>
      ))}
    </>
  );
}

export const labelForValue = (groups: PickerGroup[], value: string): string => {
  if (!value) return "";
  const match = groups.flatMap((group) => group.options).find((option) => option.value === value);
  return match?.label ?? value;
};
